import { useTranslation } from 'react-i18next'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import { ConnectorDeviceProfile, MedconnectDevice, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'

import { ConnectorHealthState } from '../../model/useConnectorHealth'
import { WIZARD_STEPS, useDeviceSetupWizard } from '../../model/useDeviceSetupWizard'
import ChooseDeviceStep from './ChooseDeviceStep'
import ConnectDeviceStep from './ConnectDeviceStep'
import ConnectorCheckStep from './ConnectorCheckStep'
import VerifyDeviceStep from './VerifyDeviceStep'

export type DeviceSetupWizardProps = {
  open: boolean
  onClose: () => void
  initialDevice?: MedconnectDevice
  profiles: ConnectorDeviceProfile[]
  isLoadingProfiles: boolean
  health: ConnectorHealthState
  release?: { currentVersion: string | null; latestVersion: string; downloadUrl?: string }
  onSave: (value: MedconnectDeviceValue) => Promise<boolean>
}

/** Guided add / edit of a local device: connector check, pick device, find it, verify it works. */
export default function DeviceSetupWizard(props: DeviceSetupWizardProps) {
  const { open, onClose, initialDevice, profiles, isLoadingProfiles, health, release, onSave } = props
  const { t } = useTranslation()
  const wizard = useDeviceSetupWizard({ initialDevice, connectorReachable: !!health.health && !health.isUnreachable, onSave })
  const profile = profiles.find((candidate) => candidate.name === wizard.draft?.name)
  const isLastStep = wizard.step === 'verify'

  return (
    <Dialog open={open} onClose={isLastStep ? onClose : undefined} fullWidth maxWidth="md">
      <DialogTitle>{t(wizard.isEditing ? 'page.configuration.MedconnectDevice.wizard.editTitle' : 'page.configuration.MedconnectDevice.wizard.addTitle')}</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Stepper activeStep={wizard.stepIndex} sx={{ mb: 4 }}>
          {WIZARD_STEPS.map((step) => (
            <Step key={step}>
              <StepLabel>{t(`page.configuration.MedconnectDevice.wizard.steps.${step}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {wizard.step === 'connector' && <ConnectorCheckStep health={health} release={release} />}
        {wizard.step === 'device' && <ChooseDeviceStep profiles={profiles} isLoading={isLoadingProfiles} selected={wizard.draft?.name ?? null} onSelect={wizard.chooseDevice} />}
        {wizard.step === 'connection' && wizard.draft && <ConnectDeviceStep draft={wizard.draft} profile={profile} onChange={wizard.updateDraft} />}
        {wizard.step === 'verify' && wizard.savedDeviceName && <VerifyDeviceStep deviceName={wizard.savedDeviceName} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {!isLastStep && <Button onClick={onClose} color="inherit">{t('common:button.cancel.label')}</Button>}
        {!isLastStep && wizard.stepIndex > 0 && !(wizard.isEditing && wizard.step === 'connection') && (
          <Button onClick={wizard.goBack}>{t('page.configuration.MedconnectDevice.wizard.back')}</Button>
        )}
        {wizard.step === 'connection' && (
          <LoadingButton variant="contained" loading={wizard.isSaving} disabled={!wizard.canContinue} onClick={() => void wizard.save()}>
            {t('page.configuration.MedconnectDevice.wizard.save')}
          </LoadingButton>
        )}
        {(wizard.step === 'connector' || wizard.step === 'device') && (
          <Button variant="contained" disabled={!wizard.canContinue} onClick={wizard.goNext}>{t('page.configuration.MedconnectDevice.wizard.next')}</Button>
        )}
        {isLastStep && <Button variant="contained" onClick={onClose}>{t('page.configuration.MedconnectDevice.wizard.finish')}</Button>}
      </DialogActions>
    </Dialog>
  )
}
